/**
 * The funnel's character renderer — one offscreen Rive mount that paints the
 * child from a {@link FunnelLook} and hands back flat PNGs.
 *
 * This is the Character Editor's rendering half with the authoring half removed.
 * It mounts the SAME `sb_character_editor.riv` child artboard, through the same
 * `editorSourceFor('child')` description, and applies a look the same way the
 * editor's apply-effect does: bound ViewModel colours from the authored skin
 * sets, the `isGirl` / `frecklesOn` / `glassesOn` booleans, the pupil / freckles
 * / glasses image slots, and the two layered hairstyles recoloured in code by
 * `buildRecoloredHair`. Nothing about how a character is assembled is
 * reinvented here — only the UI around it is gone.
 *
 * It renders OFFSCREEN and is never looked at directly: the funnel shows the
 * captured PNGs (see captureAvatar.ts), because a shelf of posters needs the
 * same child a dozen times at once and a dozen WebGL contexts is not a thing to
 * do to a phone.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useRive,
  Fit,
  Alignment,
  Layout,
  decodeImage,
  RuntimeLoader,
  DrawOptimizationOptions,
  type FileAsset,
  type ImageAsset,
} from '@rive-app/react-webgl2';
import riveWasmUrl from '@rive-app/webgl2/rive.wasm?url';
import {
  COLOR_PROP_META,
  HAIR_LAYER_ASSETS,
  REFERENCED_ASSET_URLS,
} from '@editor/contract';
import {
  NON_HAIR_IMAGE_SLOTS,
  loadAuthoredPalettes,
  type AuthoredPalettes,
} from '@editor/editorAssets';
import { editorSourceFor } from '@editor/editorSource';
import { buildBlankLayer, buildRecoloredHair } from '@editor/hairCompositor';
import { slotPlan } from './cartoon/rigLook';
import { hexToRgb } from '@editor/colorUtils';
import { resetRiveView } from '@editor/riveView';
import type { FunnelLook } from './avatarLook';
import { analytics } from './analytics';
import {
  alphaBounds,
  artboardRectToImage,
  containRect,
  copyRiveCanvas,
  cropToPng,
  collarRow,
  cropFacePng,
  type AvatarShots,
} from './captureAvatar';

RuntimeLoader.setWasmUrl(riveWasmUrl);

/** The child's own editing artboard — a full standing figure. */
const SOURCE = editorSourceFor('child');

/**
 * Offscreen canvas size. The aspect deliberately matches the child artboard
 * (733 × 1238 ⇒ 0.5921) so Rive's Contain fit leaves essentially no letterbox
 * and the artboard→image mapping the head crop needs stays a clean scale.
 */
const STAGE_W = 480;
const STAGE_H = 811;

/**
 * Where on the 1 s `CTRL smile` timeline the reveal face sits. 0.5 s is
 * neutral and 0 is the widest smile (the control runs backwards); one frame in
 * from the end, as scrubbing exactly to an end wraps a looping timeline.
 */
const SMILE_T = 1 / 240;
/** `CTRL mouthOpen` is a hold keyframe: closed before 0.5 s, open after. */
const MOUTH_OPEN_T = 0.99;

/** The head framing the editor opens the child on, in artboard pixels. */
const HEAD_RECT = { x: 14, y: 2, w: 705, h: 705 };

/**
 * How long to keep re-trying a capture that came back empty, in ms.
 *
 * An empty read is normal rather than exceptional: the composited hair lands via
 * an async `setRenderImage`, and a capture that races it reads a frame drawn
 * before the swap. Retrying costs one `drawFrame` per attempt.
 *
 * A time budget rather than a frame count, because the retry cadence is not
 * constant — see {@link scheduleRetry}. In a backgrounded tab a frame can be a
 * whole second, and "40 frames" would silently become "40 seconds".
 */
const CAPTURE_BUDGET_MS = 8000;
/**
 * How long a capture will wait for image swaps still in flight, in ms.
 *
 * Separate from the render budget above, and much longer: a pupil or a hair
 * layer still downloading on a slow phone connection is not a failed render,
 * and capturing through it bakes a child with WHITE EYES or the wrong hair
 * into every poster (seen in production, 2026-09-07). The render budget
 * decides whether Rive ever painted; this one only decides how long to let
 * the network finish.
 */
const SWAP_WAIT_MS = 45000;

/**
 * How often a slot whose image swap FAILED is tried again, and how many times.
 *
 * A swap fails on a phone for ordinary reasons — one of five layer PNGs did
 * not arrive over a cellular link, Safari refused a canvas under memory
 * pressure, `toBlob` came back empty. Before 2026-09-17 the slot's signature
 * was recorded before any of that ran, so a failed swap looked, to the capture
 * gate, exactly like a finished one: the stored avatars of Aj, Heaven, Remi,
 * Heidi, SARINE and Tatiana all show the front hair and no back layer, on
 * phone viewports only, in styles that render perfectly on a laptop. Now a
 * failure forgets the signature, so the gate keeps waiting, and the apply
 * effect runs again after a pause. Six tries cover roughly half a minute,
 * which is the swap wait; past that the capture goes ahead as it always did.
 */
const SWAP_RETRY_MS = [1000, 2000, 4000, 8000, 8000, 8000];

/**
 * Schedule the next capture attempt.
 *
 * `requestAnimationFrame` alone is a trap here: a hidden tab never fires it, so
 * a parent who switches apps while the avatar is being made comes back to a
 * funnel wedged on "Creating…" forever. Racing it against a timer keeps the
 * retry alive in the background (throttled to ~1s, which is fine — the capture
 * still works while hidden, since nothing composites the drawing buffer away).
 *
 * Returns a cancel function; whichever timer wins runs `fn` exactly once.
 */
function scheduleRetry(fn: () => void): () => void {
  let done = false;
  const run = () => { if (!done) { done = true; fn(); } };
  const raf = requestAnimationFrame(run);
  const timer = window.setTimeout(run, 100);
  return () => {
    done = true;
    cancelAnimationFrame(raf);
    clearTimeout(timer);
  };
}

export interface AvatarStageProps {
  look: FunnelLook;
  /**
   * Bumping this asks for a fresh capture of whatever is currently painted.
   * (A number rather than a callback so a parent can re-request without
   * re-rendering the stage's other props.)
   */
  captureToken: number;
  /** Fired once per satisfied `captureToken`. */
  onShots: (shots: AvatarShots) => void;
  /** Fired when a capture could not produce a non-empty image. */
  onCaptureFailed?: (reason: string) => void;
}

export function AvatarStage({ look, captureToken, onShots, onCaptureFailed }: AvatarStageProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  /** The look as of the latest render, for the capture loop (which is keyed on the token, not the look). */
  const lookRef = useRef(look);
  lookRef.current = look;
  const [artboard, setArtboard] = useState<string | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);
  const [palettes, setPalettes] = useState<AuthoredPalettes | null>(null);

  /** Rive asset name → the live image slot, filled by the asset loader. */
  const assetMap = useRef(new Map<string, ImageAsset>());
  /** The two hair slots, detected by load order (their names change per export). */
  const hairSlotOrder = useRef<string[]>([]);
  const hairSlots = useRef<{ front: string; back: string }>({ front: HAIR_LAYER_ASSETS.front, back: HAIR_LAYER_ASSETS.back });
  /** The editing rig's slot names for the shared slot plan (see cartoon/rigLook.ts). */
  const rigSlots = () => ({ ...SOURCE.imageSlots, hairFront: hairSlots.current.front, hairBack: hairSlots.current.back });
  /** Bumped whenever a slot appears, so the apply effect re-runs with it. */
  const [assetEpoch, setAssetEpoch] = useState(0);
  /** Signature of what each slot currently holds, so we only re-decode on change. */
  const appliedImages = useRef<Record<string, string>>({});
  /** In-flight async image swaps — a capture waits for these to drain. */
  const pending = useRef(0);
  /** Slots whose last swap failed: how many times, and why. Read by `__stageDebug`. */
  const failures = useRef<Record<string, { attempts: number; reason: string }>>({});
  /** Bumped by a scheduled retry, so the apply effect runs the failed swap again. */
  const [swapRetry, setSwapRetry] = useState(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (retryTimer.current) clearTimeout(retryTimer.current); }, []);

  /**
   * A swap into `slot` did not land. Forget its signature — the gate must not
   * count it as done — remember why, and try again after a pause unless this
   * slot has already used up its tries.
   */
  const swapFailed = useCallback((slot: string, sig: string, err: unknown) => {
    if (appliedImages.current[slot] === sig) delete appliedImages.current[slot];
    const reason = err instanceof Error ? err.message
      : typeof err === 'string' ? err
      : err && typeof err === 'object' && 'type' in err ? `event:${String((err as { type: unknown }).type)}`
      : String(err);
    const attempts = (failures.current[slot]?.attempts ?? 0) + 1;
    failures.current[slot] = { attempts, reason };
    analytics.event('Avatar Layer Failed', { slot, attempt: attempts, reason: reason.slice(0, 120) });
    const wait = SWAP_RETRY_MS[attempts - 1];
    if (wait === undefined || retryTimer.current) return;
    retryTimer.current = setTimeout(() => {
      retryTimer.current = null;
      setSwapRetry((n) => n + 1);
    }, wait);
  }, []);

  useEffect(() => {
    let alive = true;
    void loadAuthoredPalettes().then((p) => { if (alive) setPalettes(p); });
    return () => { alive = false; };
  }, []);

  /**
   * Asset loader, matching the editor's for the child: hair slots are detected
   * by exclusion + load order (their names carry the hairstyle, not the layer),
   * and the referenced slots that arrive with no bytes are fetched by hand.
   */
  const assetLoader = useCallback((asset: FileAsset, bytes: Uint8Array): boolean => {
    if (asset.isImage) {
      assetMap.current.set(asset.name, asset as ImageAsset);
      setAssetEpoch((e) => e + 1);
    }
    if (asset.isImage && !NON_HAIR_IMAGE_SLOTS.has(asset.name)) {
      if (!hairSlotOrder.current.includes(asset.name)) hairSlotOrder.current.push(asset.name);
      hairSlots.current.front = hairSlotOrder.current[0] ?? HAIR_LAYER_ASSETS.front;
      hairSlots.current.back = hairSlotOrder.current[1] ?? HAIR_LAYER_ASSETS.back;
      return false; // Rive decodes the original; the apply effect overrides it
    }
    if (asset.isImage && bytes.length === 0) {
      const url = REFERENCED_ASSET_URLS[asset.name];
      if (!url) return false;
      const img = asset as ImageAsset;
      pending.current += 1;
      void (async () => {
        try {
          const res = await fetch(encodeURI(url));
          const decoded = await decodeImage(new Uint8Array(await res.arrayBuffer()));
          img.setRenderImage(decoded);
          decoded.unref();
        } catch {
          /* slot stays blank — the character still renders */
        } finally {
          pending.current -= 1;
        }
      })();
      return true;
    }
    return false;
  }, []);

  const { rive, RiveComponent } = useRive({
    src: SOURCE.src,
    artboard,
    autoplay: false,
    autoBind: true,
    layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
    assetLoader,
    // The editor's reason applies verbatim: an image swap is not a "detected
    // change", so DrawOnChanged would skip the repaint that makes it visible.
    drawingOptions: DrawOptimizationOptions.AlwaysDraw,
    onLoad: () => setLoaded(true),
  });

  // Dev diagnostic, as in the editor: probe the rig from the console, e.g.
  //   __funnelRive.contents.artboards.map(a => a.name)
  //   __funnelRive.scrub('CTRL smile', 0.1); __funnelRive.drawFrame()
  //   __stageDebug()  → which slots the current look still waits on
  // Also reachable in production with `?debug` in the URL: the gate's state is
  // the one thing a stuck "Creating…" cannot be diagnosed without, and it holds
  // nothing but slot names and asset paths.
  useEffect(() => {
    if (!import.meta.env.DEV && !new URLSearchParams(location.search).has('debug')) return;
    const w = window as unknown as { __funnelRive?: unknown; __stageDebug?: () => unknown };
    w.__funnelRive = rive;
    w.__stageDebug = () => ({
      pending: pending.current,
      slots: [...assetMap.current.keys()],
      applied: { ...appliedImages.current },
      failures: { ...failures.current },
      plan: slotPlan(lookRef.current, palettes, rigSlots()).map(({ kind, slot, sig }) => ({
        kind, slot, sig, registered: !!slot && assetMap.current.has(slot), applied: slot ? appliedImages.current[slot] : undefined,
      })),
    });
  }, [rive, palettes]);

  // Pick the child's dedicated artboard once the file's contents are readable.
  useEffect(() => {
    if (!rive || !loaded || artboard) return;
    try {
      const names = (rive.contents?.artboards ?? []).map((a) => a.name);
      const picked = SOURCE.pickArtboard(names);
      if (picked && picked !== artboard) setArtboard(picked);
    } catch {
      /* read too early — the next load settles it */
    }
  }, [rive, loaded, artboard]);

  // ── Apply the look ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!rive || !loaded || !palettes) return;
    let vmi;
    try {
      vmi = rive.viewModelInstance ?? null;
    } catch {
      return; // disposed mid-remount
    }

    // Colours: the selected tone's skin set, plus the selected hair's eyebrow.
    const colours: Record<string, string> = {
      ...(palettes.skinSets[look.tone] ?? {}),
      eyebrow: palettes.hairEyebrow[look.hair],
    };
    for (const name of Object.keys(COLOR_PROP_META)) {
      const hex = colours[name];
      if (!hex) continue;
      const rgb = hexToRgb(hex);
      if (!rgb) continue;
      try {
        vmi?.color(name)?.rgb(rgb.r, rgb.g, rgb.b);
      } catch {
        /* not bound in this file — skip */
      }
    }

    // Booleans the .riv binds. Writing one it doesn't expose is a no-op.
    const bools: Record<string, boolean> = {
      isGirl: look.isGirl,
      frecklesOn: !!look.frecklesStyle,
      glassesOn: !!look.glassesStyle,
    };
    for (const [name, value] of Object.entries(bools)) {
      try {
        const b = vmi?.boolean(name);
        if (b) b.value = value;
      } catch {
        /* not bound — skip */
      }
    }

    // Expression: a happy face for the reveal. The rig is joystick-driven, so
    // the smile is set by scrubbing the injected `CTRL smile` timeline (0..1s
    // keys the joystick from -1 to +1); the control is INVERTED in the rig, a
    // LOW t is a BIG smile. The raw `SmileControl` timeline is the fallback for
    // a pre-joystick file. See admin's animationContract.ts for the contract.
    //
    // `scrub` is a no-op on a timeline the animator does not hold, so the
    // timeline is registered first with play() + pause(), exactly as the
    // editor does (a paused animation still renders its scrubbed pose). And
    // the scrubbed frame only moves the JOYSTICK; the joystick applies its own
    // pose one advance later, so one more drawFrame() is needed to see it.
    try {
      const current = rive.contents?.artboards?.find((a) => a.name === artboard) ?? rive.contents?.artboards?.[0];
      const anims = current?.animations ?? [];
      const pose: [string, number][] = [];
      if (anims.includes('CTRL smile')) pose.push(['CTRL smile', SMILE_T]);
      else if (anims.includes('SmileControl')) pose.push(['SmileControl', SMILE_T]);
      // The smile control alone is a closed-lipped curve; the open mouth is
      // what reads as a laugh. Its timeline is a hold: t < 0.5 closed, later open.
      if (anims.includes('CTRL mouthOpen')) pose.push(['CTRL mouthOpen', MOUTH_OPEN_T]);
      if (pose.length) {
        const names = pose.map(([n]) => n);
        rive.play(names);
        rive.pause(names);
        for (const [n, t] of pose) rive.scrub(n, t);
        requestAnimationFrame(() => { try { rive.drawFrame(); } catch { /* disposed */ } });
      }
    } catch {
      /* no pose timelines in this file — neutral face stands */
    }

    // Single-image slots. The pupil resolves from the pupils/ folder by the eye
    // colour's NAME (Blue.png …), falling back to the referenced default.
    const plan = slotPlan(look, palettes, rigSlots());
    for (const { slot, sig: wanted } of plan.filter((e) => e.kind === 'image')) {
      if (!slot || !wanted || appliedImages.current[slot] === wanted) continue;
      const asset = assetMap.current.get(slot);
      if (!asset) continue;
      appliedImages.current[slot] = wanted;
      pending.current += 1;
      void (async () => {
        try {
          const res = await fetch(encodeURI(wanted));
          if (!res.ok) throw new Error(`${res.status} for ${wanted}`);
          const bytes = new Uint8Array(await res.arrayBuffer());
          if (appliedImages.current[slot] !== wanted) return; // superseded while fetching
          const decoded = await decodeImage(bytes);
          asset.setRenderImage(decoded);
          // Our reference is deliberately NOT released. A look is applied at
          // least twice per visit (the default before the analysis, the real
          // one after), and releasing it after the SECOND swap into the same
          // slot left the slot EMPTY: the child came out with the front hair
          // and no back layer (Alex, 2026-09-17, on a phone — pigtails in the
          // cartoon, whose player never releases, none on the avatar). A few
          // decoded images kept alive per visit cost nothing.
          rive.drawFrame();
        } catch (err) {
          swapFailed(slot, wanted, err);
        } finally {
          pending.current -= 1;
        }
      })();
    }

    // Layered hair: recolour each style's grayscale stack with the selected
    // colour's shade ramp and push one composited PNG into each slot. A style of
    // null EMPTIES the slot (a transparent PNG), which is what renders bald —
    // not-pushing would leave the .riv's own embedded hair in place.
    const shades = palettes.hairShades[look.hair] ?? [];
    for (const { slot, sig, layers } of plan.filter((e) => e.kind === 'hair')) {
      if (!slot || !sig) continue;
      const blank = sig === 'none';
      if (appliedImages.current[slot] === sig) continue;
      const asset = assetMap.current.get(slot);
      if (!asset) continue;
      appliedImages.current[slot] = sig;
      pending.current += 1;
      void (async () => {
        try {
          const bytes = blank ? await buildBlankLayer() : await buildRecoloredHair(layers, shades);
          // A newer look may have superseded this composite while it built.
          if (appliedImages.current[slot] !== sig) return;
          if (!bytes) throw new Error(`composite of ${layers.length} layers produced nothing`);
          const decoded = await decodeImage(bytes);
          asset.setRenderImage(decoded);
          // Our reference is deliberately NOT released. A look is applied at
          // least twice per visit (the default before the analysis, the real
          // one after), and releasing it after the SECOND swap into the same
          // slot left the slot EMPTY: the child came out with the front hair
          // and no back layer (Alex, 2026-09-17, on a phone — pigtails in the
          // cartoon, whose player never releases, none on the avatar). A few
          // decoded images kept alive per visit cost nothing.
          rive.drawFrame();
        } catch (err) {
          swapFailed(slot, sig, err);
        } finally {
          pending.current -= 1;
        }
      })();
    }

    try {
      rive.drawFrame();
    } catch {
      /* disposed */
    }
  }, [rive, loaded, palettes, look, assetEpoch, swapRetry, swapFailed]);

  // ── Capture ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!captureToken || !rive || !loaded || !palettes) return;
    let cancelled = false;
    let cancelPending: (() => void) | null = null;
    let deadline = performance.now() + CAPTURE_BUDGET_MS;
    /**
     * A hidden tab does not paint, so Rive's buffer stays empty and every read
     * comes back blank — through no fault of the render. Spending the budget on
     * that would hand a parent who switched apps a funnel with NO character in
     * it, silently. So hidden time doesn't count: the deadline is pushed along
     * for as long as the tab is away, and the attempt that follows the tab
     * coming back is the one that decides.
     */
    const inBudget = () => {
      if (document.hidden) {
        deadline = performance.now() + CAPTURE_BUDGET_MS;
        return true;
      }
      return performance.now() < deadline;
    };
    const retry = () => { cancelPending = scheduleRetry(attempt); };
    // Come back immediately when the tab does, rather than on the next poll.
    const onVisible = () => { if (!document.hidden && !cancelled) { cancelPending?.(); retry(); } };
    document.addEventListener('visibilitychange', onVisible);

    const swapDeadline = performance.now() + SWAP_WAIT_MS;
    /**
     * Every slot holds what the CURRENT look asks for, and nothing is in flight.
     *
     * "Nothing in flight" alone was not enough: the .riv's image slots register
     * asynchronously as the file's assets decode, and a look applied before a
     * slot existed is simply skipped — nothing pending, nothing recorded — so
     * the capture went ahead and photographed the rig's own embedded hair. Seen
     * in production on 2026-09-16: the analysis said blonde high ponytail, the
     * stored avatar had the built-in brown bob, the live stage caught up a
     * moment later. The apply effect re-runs when the slot appears; this makes
     * the capture wait for it.
     */
    const settled = () => {
      if (pending.current > 0) return false;
      for (const { slot, sig } of slotPlan(lookRef.current, palettes, rigSlots())) {
        if (!sig) continue;
        if (!slot || !assetMap.current.has(slot)) return false;
        if (appliedImages.current[slot] !== sig) return false;
      }
      return true;
    };
    const attempt = () => {
      if (cancelled) return;
      // Wait for the look to actually land in the rig — a capture taken mid-swap
      // shows the previous hairstyle, or no pupils at all, which is worse than
      // waiting. Hidden time does not count here either.
      if (!settled() && (document.hidden || performance.now() < swapDeadline)) {
        // The render budget must not tick down while we merely wait on the network.
        deadline = Math.max(deadline, performance.now() + CAPTURE_BUDGET_MS);
        retry();
        return;
      }

      const canvas = wrapRef.current?.querySelector('canvas');
      if (!canvas) {
        if (inBudget()) retry();
        else onCaptureFailed?.('The character canvas never mounted.');
        return;
      }

      // SAME-TASK RULE: draw and copy with nothing awaited in between.
      let copy: HTMLCanvasElement | null = null;
      try {
        resetRiveView(rive);
        rive.drawFrame();
        copy = copyRiveCanvas(canvas as HTMLCanvasElement);
      } catch {
        copy = null;
      }

      const bounds = copy ? alphaBounds(copy) : null;
      if (!copy || !bounds) {
        if (inBudget()) retry();
        else onCaptureFailed?.('The character rendered blank.');
        return;
      }

      const drawn = containRect(copy.width, copy.height, SOURCE.refW, SOURCE.refH);
      const full = cropToPng(copy, bounds);
      const head = cropToPng(copy, artboardRectToImage(HEAD_RECT, drawn, SOURCE.refW, SOURCE.refH));
      if (!full || !head) {
        if (inBudget()) retry();
        else onCaptureFailed?.('The character could not be cropped.');
        return;
      }
      const collar = collarRow(copy, bounds);
      const face = collar === null ? null : cropFacePng(copy, bounds, collar);
      const faceAspect = collar === null ? 1 : bounds.w / (collar + 2 - bounds.y);
      onShots({ full, head, face, fullAspect: bounds.w / bounds.h, faceAspect });
    };

    retry();
    return () => {
      cancelled = true;
      cancelPending?.();
      document.removeEventListener('visibilitychange', onVisible);
    };
    // `look` is intentionally absent: a capture is requested by token, and the
    // apply effect above has already run for whatever look is current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureToken, rive, loaded, palettes]);

  return (
    <div
      ref={wrapRef}
      aria-hidden
      style={{
        position: 'fixed',
        left: -10000,
        top: 0,
        width: STAGE_W,
        height: STAGE_H,
        pointerEvents: 'none',
      }}
    >
      <RiveComponent style={{ width: '100%', height: '100%' }} />
    </div>
  );
}
